import { Injectable } from '@angular/core';
import { Presence } from '@app/core/interfaces/c5/presence';
import { RichPresenceDef } from '@app/core/interfaces/c5/rich-presence-def';
import { DeviceNumberMapping } from '@app/core/interfaces/device-number-mapping';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import {
  PresenceProfile,
  PresenceProfileSettingsUpdate,
  RichPresenceAcdData,
  RichPresenceAcdQueues,
} from '@app/core/interfaces/presence-profile';
import { RichPresenceProfileRepository } from '@app/core/repositories/rich-presence-profile.repository';
import { RichPresenceStateRepository } from '@app/core/repositories/rich-presence-state.repository';
import { DeviceEnhancerService } from '@app/shared/device-enhancer/device-enhancer.service';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { debounce, toMap } from '@app/utils/common';
import { DateUtils } from '@app/utils/date';
import { PortalError, portalError } from '@app/utils/error/portal-error';
import { groupToMap } from '@app/utils/group-to-map';
import { wrapError } from '@app/utils/rxjs';
import { Features } from '@business/feature/interfaces/features';
import { RichPresenceStateViewModel } from '@business/rich-presence/components/rich-presence-state/rich-presence-state-view-model';
import { RichPresenceProfileUpdate } from '@business/rich-presence/models/rich-presence-profile';
import {
  PresenceProfileErrors,
  PresenceProfileUserFeaturesVM,
  PresenceProfileViewModel,
  RichPresenceAcdDataVM,
  RichPresenceAcdQueueDataVM,
  RichPresenceAcdQueuesVM,
  RichPresenceDeviceNumberMappingEntry,
  RichPresenceDevicesNumberMappings,
} from '@business/rich-presence/models/rich-presence-profile-view-model';
import { RichPresenceState } from '@business/rich-presence/models/rich-presence-state';
import { forkJoin, iif, merge, Observable, of, switchMap } from 'rxjs';
import { delayWhen, first, map, mergeMap, take, tap } from 'rxjs/operators';
// import { isBefore, isEqual } from 'date-fns';
import { QueueAgent } from '@app/core/interfaces/c5/acd/queue-agent';
import { Device, DeviceKind } from '@app/core/interfaces/c5/device';
import { Activity, EnhancedActivity } from '@app/core/interfaces/c5/enumerations';
import { Feature } from '@app/core/interfaces/c5/feature';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { PresenceProfileApplicationError } from '@app/core/interfaces/presence-profile-application-error';
import { PortalEventContext } from '@app/core/models/portal-event-context';
import {
  ApplicationEventService,
  EndUserPhoneNumberChangedEvent,
  EndUserRichPresenceStatusChangedEvent,
  FeatureChangedEvent,
  OptionalFeatureChangedEvent,
} from '@app/core/services/application-events';
import { PhoneNumbersGrouped } from '@app/shared/device-enhancer/phone-numbers-grouped';
import { ParsedError } from '@app/shared/models/parsed-error';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { PhoneNumber } from '@business/phone-number/models/phone-number';

@Injectable({
  providedIn: 'root',
})
export class RichPresenceService {
  private _endUserPresence: Cached<Presence> | null = null;

  get endUserPresence(): Cached<Presence> {
    if (this._endUserPresence == null) {
      const [cached, connector] = this.cachedHelperService.routeAwareConnectedCached({
        context: PortalEventContext.END_USER_RICH_PRESENCE,
        dataProducer: () => this.richPresenceStateRepository.get(),
        id: ([userId]) => `user:${userId}:rich-presence-state`,
        rememberTtl: 10_000,
        routeParameters: ['userId'],
      });

      this._endUserPresence = cached;

      connector.subscribe({
        next: () => {
          this.log.debug('Rich presence state has changed');
          this.reloadPresence();
        },
      });
    }

    return this._endUserPresence;
  }

  private _endUserPresenceState: Cached<RichPresenceStateViewModel> | null = null;

  get endUserPresenceState(): Cached<RichPresenceStateViewModel> {
    this._endUserPresenceState ??= cachedGroup(
      [this.endUserPresence, this.availablePresenceStates],
      ([state, presenceStateDefinitions]) => of(this.mapToViewModel(state, presenceStateDefinitions)),
      {
        id: 'rich-presence:end-user-presence-state',
        watchSourcesLoadingState: true,
      }
    );

    return this._endUserPresenceState;
  }

  private _availablePresenceStates: Cached<RichPresenceDef[]> | null = null;

  get availablePresenceStates(): Cached<RichPresenceDef[]> {
    this._availablePresenceStates ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.richPresenceStateRepository.listAvailablePresenceStates(),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:available-rich-presence-states`,
      routeParameters: ['enterpriseId'],
    });

    return this._availablePresenceStates;
  }

  private _presenceProfiles: Cached<PresenceProfile[]> | null = null;

  get presenceProfiles(): Cached<PresenceProfile[]> {
    this._presenceProfiles ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.richPresenceProfileRepository.list(),
      id: ([userId]) => `user:${userId}:presence-profiles`,
      routeParameters: ['userId'],
    });
    return this._presenceProfiles;
  }

  private _presenceProfilesViewModel: Cached<PresenceProfileViewModel[]> | null = null;

  get presenceProfilesViewModel(): Cached<PresenceProfileViewModel[]> {
    this._presenceProfilesViewModel ??= cachedGroup(
      [
        this.availablePresenceStates,
        this.presenceProfiles,
        this.endUserContextService.devices,
        this.endUserContextService.endUser,
        this.endUserContextService.features,
        this.endUserContextService.phoneNumbers,
        this.endUserContextService.acdAgents,
      ],
      ([
        presenceStates,
        profiles,
        currentDevices,
        endUser,
        currentFeatures,
        currentPhoneNumbers,
        currentAcdAgents,
      ]) => {
        const currentDevicesMap = toMap(currentDevices, 'id');
        const currentPhoneNumberMap = toMap(currentPhoneNumbers, 'id');

        const viewModel: PresenceProfileViewModel[] = profiles.map((profile) =>
          this.mapProfile({
            currentDevicesMap,
            endUser,
            currentFeatures,
            currentPhoneNumberMap,
            profile,
            presenceStates,
            currentAcdAgents,
          })
        );

        return of(viewModel);
      },
      {
        id: 'rich-presence:profiles',
      }
    );

    return this._presenceProfilesViewModel;
  }

  private _endUserPresenceProfile: Cached<PresenceProfile> | null = null;

  get endUserPresenceProfile(): Cached<PresenceProfile> {
    this._endUserPresenceProfile ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([, profileId]) => this.richPresenceProfileRepository.get(Number(profileId)),
      id: ([userId, profileId]) => `user:${userId}:presence-profiles:${profileId}`,
      routeParameters: ['userId', 'profileId'],
    });

    return this._endUserPresenceProfile;
  }

  private _endUserPresenceProfileViewModel: Cached<PresenceProfileViewModel> | null = null;

  get endUserPresenceProfileViewModel(): Cached<PresenceProfileViewModel> {
    this._endUserPresenceProfileViewModel ??= cachedGroup(
      [
        this.availablePresenceStates,
        this.endUserContextService.endUser,
        this.endUserContextService.features,
        this.endUserContextService.phoneNumbers,
        this.endUserContextService.devices,
        this.endUserPresenceProfile,
        this.endUserContextService.acdAgents,
      ],
      ([presenceStates, endUser, currentFeatures, phoneNumbers, devices, profile, currentAcdAgents]) => {
        const currentDevicesMap = toMap(devices, 'id');
        const currentPhoneNumberMap = toMap(phoneNumbers, 'id');

        return of(
          this.mapProfile({
            currentDevicesMap,
            endUser,
            currentFeatures,
            currentPhoneNumberMap,
            profile,
            presenceStates,
            currentAcdAgents,
          })
        );
      },
      {
        id: 'end-user:presence-profile-view-model',
      }
    );

    return this._endUserPresenceProfileViewModel;
  }

  private log = loggerFor('RichPresenceService');

  /**
   * Used to debounce rich presence updates after the presence state has been changed by the user.
   */
  private reloadPresence = debounce(() => {
    this._endUserPresence?.data$
      .pipe(
        take(1),
        delayWhen(() => this._endUserPresence.reload(false)),
        switchMap((previousState) =>
          this._endUserPresence.data$.pipe(
            take(1),
            map((newState) => previousState.activity !== newState.activity)
          )
        )
      )
      .subscribe((activityChanged) => {
        if (activityChanged) {
          this.applicationEventService.emit(EndUserRichPresenceStatusChangedEvent());
        }
      });
  }, 500);

  constructor(
    private cachedHelperService: CachedHelperService,
    private deviceEnhancerService: DeviceEnhancerService,
    private enterpriseContextService: EnterpriseContextService,
    private endUserContextService: EndUserContextService,
    private richPresenceStateRepository: RichPresenceStateRepository,
    private richPresenceProfileRepository: RichPresenceProfileRepository,
    private applicationEventService: ApplicationEventService
  ) {
    merge(
      this.applicationEventService.on$(FeatureChangedEvent),
      this.applicationEventService.on$(OptionalFeatureChangedEvent),
      this.applicationEventService.on$(EndUserPhoneNumberChangedEvent)
    ).subscribe(() => {
      // profiles as well as the profile cache must be reloaded or cleared
      this._presenceProfiles?.reload();
      this._endUserPresenceProfile?.reload();
    });
  }

  deleteProfile(profileId: number) {
    return this.richPresenceProfileRepository.delete(profileId).pipe(
      delayWhen(() => {
        const r1 = this._presenceProfiles?.reload() ?? of(undefined);
        const r2 = this.enterpriseContextService.endUsers.reload();
        const r3 = (
          this._presenceProfiles?.data$.pipe(
            first(),
            map((pps) => pps.length === 0)
          ) ?? of(false)
        ).pipe(
          mergeMap((shouldReload) => {
            if (shouldReload) {
              this.endUserContextService.endUser.reload();
            }
            return of(undefined);
          })
        );
        return forkJoin([r1, r2, r3]);
      }),
      wrapError('rich-presence-profile:delete-profile')
    );
  }

  enableProfiles(): Observable<PresenceProfile[]> {
    return this.richPresenceProfileRepository.activate().pipe(
      delayWhen(() => {
        const r1 = this._presenceProfiles?.reload() ?? of(undefined);
        const r2 = this.endUserContextService.endUser.reload();
        const r3 = this.enterpriseContextService.endUsers.reload();
        return forkJoin([r1, r2, r3]);
      })
    );
  }

  setEndUserPresenceState(state: RichPresenceState): Observable<void> {
    return this.richPresenceStateRepository
      .update({
        activity: state.activity,
        note: state.note,
      })
      .pipe(
        tap(() => {
          this.reloadPresence();
          this._presenceProfiles?.reload();
          this._endUserPresenceProfile?.reload();
        })
      );
  }

  toggleProfileAsDefault(profile: PresenceProfile): Observable<void> {
    return this.richPresenceProfileRepository.setAsDefault(profile.id, profile.default).pipe(
      tap(() => {
        this._endUserPresenceProfile?.reload();
      }),
      delayWhen(() => this._presenceProfiles?.reload() ?? of(undefined)),
      wrapError('rich-presence-service:toggle-profile-as-default')
    );
  }

  updateProfile(profile: RichPresenceProfileUpdate): Observable<PresenceProfile> {
    return iif(
      () => profile.id != null,
      this.richPresenceProfileRepository.update(profile.id, {
        active: profile.active,
        description: profile.description,
      }),
      this.richPresenceProfileRepository.create({
        active: profile.active,
        c5Activity: profile.activity,
        description: profile.description,
        basedOnProfileWithId: profile.copyFromProfileId,
      })
    ).pipe(
      tap(() => {
        this._endUserPresenceProfile?.reload();
      }),
      delayWhen(() => this._presenceProfiles?.reload() ?? of(undefined)),
      wrapError('rich-presence-service:update-profile')
    );
  }

  updateProfileSettings(
    type: string,
    profileId: number,
    data: PresenceProfileSettingsUpdate
  ): Observable<void> {
    return this.richPresenceProfileRepository.updateSettings(type, profileId, data).pipe(
      tap(() => {
        this._presenceProfiles?.reload();
      }),
      delayWhen(() => this._endUserPresenceProfile?.reload() ?? of(undefined))
    );
  }

  private calculateFeatures(
    profile: PresenceProfile,
    errors: PresenceProfileErrors
  ): PresenceProfileUserFeaturesVM {
    // #60947 hide `CALL_FORWARD_UNREGISTERED` because this one is a copy of `CALL_FORWARD_TIMEOUT`
    const userFeatures = profile.userFeatures.features.filter(
      (f) => f.name !== FeatureName.CALL_FORWARD_UNREGISTERED
    );

    return {
      active: profile.userFeatures.active,
      features: userFeatures.map((feature) => {
        let dataKey =
          feature.name
            .split('_')
            .map((part) => part.toLowerCase())
            .map((part, index) => (index > 0 ? part.substring(0, 1).toUpperCase() + part.substring(1) : part))
            .join('') + 'Data';

        // of course, at least one does not play by the rules ^^
        if (feature.name === FeatureName.CALL_BACK_NUMBER) {
          dataKey = 'callBackData';
        }

        return {
          active: feature.active,
          name: feature.name,
          featureJson: feature.featureJson,
          dataKey,
          missing: errors.missingFeatures?.some((missingFeature) => missingFeature.featName === feature.name),
        };
      }),
    };
  }

  private calculateMissingFeatures(features: Feature[], userFeatures: Features): Feature[] {
    const userFeaturesByName = toMap(Object.values(userFeatures), 'featName');

    return features.filter((feature) => userFeaturesByName[feature.featName] == null);
  }

  private calculateProfileErrors(
    profile: PresenceProfile,
    features: Features,
    devicesNumberMappings: RichPresenceDevicesNumberMappings,
    acdData: RichPresenceAcdDataVM
  ): PresenceProfileErrors {
    const errors: PresenceProfileErrors = {
      hasErrors: false,
      missingActiveFeatures: false,
      missingAgents: false,
    };
    const missingFeatures = this.calculateMissingFeatures(
      profile.userFeatures.features.map((f) => f.featureJson),
      features
    );

    if (devicesNumberMappings.missingDevices.size > 0) {
      errors.missingDevices = Array.from(devicesNumberMappings.missingDevices);
    }

    if (devicesNumberMappings.missingPhoneNumbers.size > 0) {
      errors.missingPhoneNumbers = Array.from(devicesNumberMappings.missingPhoneNumbers.values());
    }

    if (missingFeatures.length > 0) {
      errors.missingFeatures = missingFeatures;

      if (profile.userFeatures.active === true) {
        const missingFeatureNames = missingFeatures.map((f) => f.featName);
        errors.missingActiveFeatures = profile.userFeatures.features.some(
          (f) => f.active && missingFeatureNames.some((fn) => fn === f.name)
        );
      }
    }

    errors.missingAgents = acdData.invalid;

    errors.hasErrors =
      errors.missingDevices != null ||
      errors.missingActiveFeatures ||
      errors.missingPhoneNumbers != null ||
      errors.missingAgents;
    return errors;
  }

  private getDeviceNumberMapping(
    profile: PresenceProfile,
    deviceMap: Record<number, Device>,
    phoneNumberMap: Record<number, PhoneNumber>,
    endUser: EndUserData,
    features: Features
  ): RichPresenceDevicesNumberMappings {
    const devicesNumberMappings = profile.userDevicesNumberMappings.devicesNumberMappings
      .filter((mapping) => mapping.deviceNumberMappingJsArray.length > 0)
      .map((m) => m.deviceNumberMappingJsArray)
      .reduce((a, b) => a.concat(b), []);

    const deviceIdActivityMap: Map<number, boolean> = new Map(
      profile.userDevicesNumberMappings.devicesNumberMappings.map((dnm) => [dnm.deviceId, dnm.active])
    );

    const { deviceNumbers, missingDevices, missingPhoneNumbers, phoneNumbers } = this.getDevicesAndNumbers(
      deviceIdActivityMap,
      devicesNumberMappings,
      deviceMap,
      phoneNumberMap
    );

    const devicesNumberMappingsMap: Map<number, RichPresenceDeviceNumberMappingEntry> = groupToMap(
      devicesNumberMappings,
      (item) => item.deviceId,
      (deviceId, mappings) => {
        const entry: RichPresenceDeviceNumberMappingEntry = {
          active: deviceIdActivityMap.get(deviceId),
          device: deviceMap[deviceId],
          deviceId,
          deviceMissing: missingDevices.has(deviceId),
          isPhoneNumberAssignmentAllowed: true,
          identity: {
            external: undefined,
            sms: undefined,
          },
          mappings: mappings.map((mapping) => {
            return {
              ...mapping,
              phoneNumberMissing: missingPhoneNumbers.has(mapping.phoneNumberId),
            };
          }),
          missingPhoneNumbers: [],
          phoneNumbersGrouped: {} as PhoneNumbersGrouped,
        };

        if (entry.device != null) {
          entry.device.phoneNumbers = Object.values(deviceNumbers[entry.device.id] ?? {});

          const enhancedDevice = this.deviceEnhancerService.enhance(
            entry.device,
            [],
            [],
            endUser,
            [],
            features,
            []
          );

          entry.isPhoneNumberAssignmentAllowed = enhancedDevice.isPhoneNumberAssignmentAllowed;

          entry.identity.external = {
            invalid:
              enhancedDevice.identity.external == null ||
              missingPhoneNumbers.has(enhancedDevice.identity.external.id),
            phoneNumber: enhancedDevice.identity.external,
          };

          if (enhancedDevice.kind === DeviceKind.MOBILE) {
            const phoneNumber = Object.values(endUser.user.phoneNumbers).find(
              (phoneNumber) => phoneNumber.e164 === profile.smsMoE164
            );

            entry.identity.sms = {
              e164: profile.smsMoE164,
              invalid: phoneNumber == null,
              phoneNumber,
            };
          } else {
            entry.identity.sms = null;
          }

          for (const phoneNumber of entry.device.phoneNumbers) {
            if (missingPhoneNumbers.has(phoneNumber.id)) {
              entry.missingPhoneNumbers.push(phoneNumber);
            }
          }

          entry.phoneNumbersGrouped = enhancedDevice.phoneNumbersGrouped;
        }

        return entry;
      }
    );

    const entries = Array.from(devicesNumberMappingsMap.values()).filter(
      (e) => e.isPhoneNumberAssignmentAllowed
    );

    return {
      active: profile.userDevicesNumberMappings.active,
      devicesNumberMappings: entries,
      invalid: entries
        .filter((dnm) => dnm.active === true)
        .some(
          (entry) =>
            (entry.identity.external?.invalid ?? false) ||
            (entry.identity.sms?.invalid ?? false) ||
            missingPhoneNumbers.size > 0 ||
            missingDevices.size > 0
        ),
      missingDevices,
      missingPhoneNumbers,
      phoneNumberMap: phoneNumbers,
      phoneNumbers: Object.values(phoneNumbers),
    };
  }

  private getDevicesAndNumbers(
    deviceIdActivityMap: Map<number, boolean>,
    deviceNumberMappings: DeviceNumberMapping[],
    deviceMap: Record<number, Device>,
    phoneNumberMap: Record<number, PhoneNumber>
  ) {
    const devices: Record<number, Device> = {};
    const deviceNumbers: Record<number, Record<number, PhoneNumber>> = {};
    const phoneNumbers: Record<number, PhoneNumber> = {};
    const missingDevices = new Set<number>();
    const missingPhoneNumbers = new Map<number, PhoneNumber>();

    for (let mapping of deviceNumberMappings) {
      if (devices[mapping.deviceId] == null) {
        if (deviceMap[mapping.deviceId] == null) {
          missingDevices.add(mapping.deviceId);
        } else {
          devices[mapping.deviceId] = deviceMap[mapping.deviceId];
          deviceNumbers[mapping.deviceId] = {};
        }
      }

      if (phoneNumbers[mapping.phoneNumberId] == null) {
        if (phoneNumberMap[mapping.phoneNumberId] == null) {
          missingPhoneNumbers.set(mapping.phoneNumberId, mapping.phoneNumber);
        } else {
          phoneNumbers[mapping.phoneNumberId] = phoneNumberMap[mapping.phoneNumberId];
        }
      }

      if (deviceNumbers[mapping.deviceId] == null) {
        deviceNumbers[mapping.deviceId] = {};
      }

      deviceNumbers[mapping.deviceId][mapping.phoneNumberId] = {
        ...mapping.phoneNumber,
        // Override the direction because the direction of the phone number is the same for every
        // device. The mapping contains the direction of the phone number for a specific device.
        dir: mapping.dir,
      };
    }

    return {
      deviceNumbers,
      missingDevices,
      missingPhoneNumbers,
      phoneNumbers,
    };
  }

  private concatenateErrorWithC5Error(
    applicationError: PresenceProfileApplicationError
  ): PresenceProfileApplicationError {
    if (applicationError?.c5Error) {
      // newlines and tabs are escaped in C5 error (json response). Going to reactivate newlines and tabs. With CSS and `white-space: pre-wrap` they will be applied.
      applicationError.error +=
        '\n- C5Error: ' + applicationError.c5Error.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    }

    return applicationError;
  }

  private mapApplicationErrors(profile: PresenceProfile): PortalError[] {
    let errors: PortalError[] = [];

    if (profile.lastApplicationSuccessful === false && profile.lastErrorData != null) {
      const lastAppliedOn = DateUtils.parse(profile.lastAppliedOn);
      const lastErrorOn = DateUtils.parse(profile.lastErrorOn);

      profile.lastErrorData.devices = profile.lastErrorData.devices.map((e) =>
        this.concatenateErrorWithC5Error(e)
      );
      profile.lastErrorData.features = profile.lastErrorData.features.map((e) =>
        this.concatenateErrorWithC5Error(e)
      );
      profile.lastErrorData.smsMo = this.concatenateErrorWithC5Error(profile.lastErrorData.smsMo);
      profile.lastErrorData.acd = profile.lastErrorData.acd.map((e) => this.concatenateErrorWithC5Error(e));

      // if (isBefore(lastAppliedOn, lastErrorOn) || isEqual(lastAppliedOn, lastErrorOn)) {
      const lastErrorData = profile.lastErrorData;

      if (lastErrorData?.devices != null) {
        errors.push(
          ...lastErrorData.devices
            .filter((e) => e.message.endsWith('.backup') === false)
            .map((applicationError) => {
              return portalError({
                // cause: new Error(applicationError.error),
                cause: new ParsedError({
                  header: applicationError.message,
                  response: { exception: applicationError.error },
                }),
                id: 'richPresence.application-error.device-number-mapping',
                i18n: applicationError.message,
              });
            })
        );
      }

      if (lastErrorData?.features != null) {
        errors.push(
          ...lastErrorData.features
            .filter((e) => e.message.endsWith('.backup') === false)
            .map((applicationError) => {
              return portalError({
                // cause: new Error(applicationError.error),
                cause: new ParsedError({
                  header: applicationError.message,
                  response: { exception: applicationError.error },
                }),
                id: 'richPresence.application-error.feature',
                i18n: applicationError.message,
              });
            })
        );
      }

      if (lastErrorData?.smsMo != null) {
        errors.push(
          portalError({
            // cause: new Error(lastErrorData.smsMo.error),
            cause: new ParsedError({
              header: lastErrorData.smsMo.message,
              response: { exception: lastErrorData.smsMo.error },
            }),
            id: 'richPresence.application-error.device-smsmo',
            i18n: lastErrorData.smsMo.message,
          })
        );
      }

      if (lastErrorData?.acd != null) {
        errors.push(
          ...lastErrorData.acd
            .filter((e) => e.message.endsWith('.backup') === false)
            .map((applicationError) => {
              let i18n = applicationError.message;
              let i18nParams: StringMap<string> = null;
              const split = applicationError.message.split('.');
              if (split.length === 3) {
                i18n = `${split[0]}.${split[1]}`;
                i18nParams = { name: split[2] };
              }
              return portalError({
                // cause: new Error(applicationError.error),
                cause: new ParsedError({
                  header: applicationError.message,
                  response: { exception: applicationError.error },
                }),
                id: 'richPresence.application-error.acd',
                i18n: {
                  key: i18n,
                  parameters: i18nParams,
                },
              });
            })
        );
      }
    }

    return errors;
  }

  private mapProfile({
    currentDevicesMap,
    endUser,
    currentFeatures,
    currentPhoneNumberMap,
    profile,
    presenceStates,
    currentAcdAgents,
  }: MapRichPresenceProfileOptions): PresenceProfileViewModel {
    let state = presenceStates.find((state) => state.activity === profile.c5Activity)!;
    if (state == null) {
      state = {
        activity: Activity.UNKNOWN,
        refActivity: Activity.UNKNOWN,
      } as RichPresenceDef;
    }
    const devicesNumberMappings = this.getDeviceNumberMapping(
      profile,
      currentDevicesMap,
      currentPhoneNumberMap,
      endUser,
      currentFeatures
    );
    const acdData = this.mapAcdData(profile.acdData, currentAcdAgents);
    const errors = this.calculateProfileErrors(profile, currentFeatures, devicesNumberMappings, acdData);
    const profileFeatures = this.calculateFeatures(profile, errors);
    const lastApplicationErrors = this.mapApplicationErrors(profile);

    return {
      _original: profile,
      active: profile.active,
      activity: profile.c5Activity,
      default: profile.default,
      deviceMap: currentDevicesMap,
      devicesNumberMappings,
      id: profile.id,
      description: profile.description,
      features: profileFeatures,
      errors,
      lastApplicationOn: profile.lastAppliedOn,
      lastApplicationErrors,
      lastApplicationErrorOn: profile.lastErrorOn,
      lastApplicationSuccessful: profile.lastApplicationSuccessful,
      name: profile.name,
      phoneNumberMap: currentPhoneNumberMap,
      refActivity: state.refActivity,
      stateDef: state,
      stateDefActive: state.activity !== Activity.UNKNOWN,
      endUserIsAcdQueueAgent: endUser.isAcdQueueAgent,
      endUserIsCallCenterQueueAgent: endUser.isCallCenterAgent,
      acdData,
      acdAgents: currentAcdAgents,
    };
  }

  private mapAcdData(acdCcData: RichPresenceAcdData, acdAgents: QueueAgent[]): RichPresenceAcdDataVM {
    const agentsMap = toMap(acdAgents, 'queueId');
    const cc = this.mapAcdQueuesData(acdCcData.cc, agentsMap);
    const acd = this.mapAcdQueuesData(acdCcData.acd, agentsMap);

    return {
      cc,
      acd,
      invalid: cc?.invalid || acd?.invalid,
    };
  }

  private mapAcdQueuesData(
    data: RichPresenceAcdQueues,
    agentsMap: StringMap<QueueAgent>
  ): RichPresenceAcdQueuesVM {
    let result: RichPresenceAcdQueuesVM = null;
    if (data != null) {
      let invalid = false;
      const qs: RichPresenceAcdQueueDataVM[] = data.queues.map((q) => {
        if (agentsMap[q.queueId] == null) {
          // report data as invalid only for active agent state setting
          if (q.active === true) {
            invalid = true;
          }
          return {
            ...q,
            error: 'richPresence.profile.acd.error.agentNoLongerExists',
          };
        }
        return q;
      });
      result = {
        active: data.active,
        invalid,
        queues: qs,
      };
    }
    return result;
  }

  private mapToViewModel(state: Presence, definitions: RichPresenceDef[]): RichPresenceStateViewModel {
    const stateDefinition = definitions.find((definition) => definition.activity === state.activity);

    return {
      activity: stateDefinition?.refActivity ?? stateDefinition?.activity ?? state.activity,
      enhancedActivity: state.enhancedActivity === EnhancedActivity.NONE ? null : state.enhancedActivity,
      label: stateDefinition?.label,
      note: state.note,
      realActivity: state.activity,
    };
  }
}

interface MapRichPresenceProfileOptions {
  currentDevicesMap: Record<number, Device>;
  endUser: EndUserData;
  currentFeatures: Features;
  currentPhoneNumberMap: Record<number, PhoneNumber>;
  profile: PresenceProfile;
  presenceStates: RichPresenceDef[];
  currentAcdAgents: QueueAgent[];
}
